import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { StepperInput } from '@shared/components/ui/StepperInput';
import { useToast } from '@shared/hooks/use-toast';
import { faArrowRight, faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdjustWarehouseMaterialStock } from '../../hooks/useAdjustWarehouseMaterialStock';

export interface AdjustStockModalRef {
  open: (
    options: {
      productId: number;
      locationId: number;
      locationCode: string;
      currentQty?: number;
    },
    onConfirm?: () => void,
  ) => void;
}

export const AdjustStockModal = forwardRef<AdjustStockModalRef>((_, ref) => {
  const { t } = useTranslation('materialAlmacenModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const { toast } = useToast();
  const confirmRef = useRef<() => void>(() => {});

  const [productId, setProductId] = useState<number | null>(null);
  const [locationId, setLocationId] = useState<number | null>(null);
  const [locationCode, setLocationCode] = useState('');
  const [currentQty, setCurrentQty] = useState(0);
  const [quantity, setQuantity] = useState(0);
  const [reason, setReason] = useState('');

  const { mutateAsync: adjustStock, isPending } = useAdjustWarehouseMaterialStock();

  useImperativeHandle(ref, () => ({
    open: (opts, onConfirm) => {
      setProductId(opts.productId);
      setLocationId(opts.locationId);
      setLocationCode(opts.locationCode);
      setCurrentQty(opts.currentQty ?? 0);
      setQuantity(0);
      setReason('');
      confirmRef.current = onConfirm || (() => {});
      modalRef.current?.openModal();
    },
  }));

  const newStock = currentQty + quantity;
  const minQuantity = -currentQty;

  const handleQuantityChange = (value: number) => {
    if (value < minQuantity) {
      setQuantity(minQuantity);
      return;
    }
    if (value > 99999) {
      setQuantity(99999);
      return;
    }
    setQuantity(value);
  };

  const handleConfirm = async () => {
    if (!productId || !locationId || quantity === 0) {
      toast({
        title: t('modal.adjust_stock.error_title'),
        description: t('modal.adjust_stock.validation_error'),
        variant: 'destructive',
      });
      return;
    }

    if (newStock < 0) {
      toast({
        title: t('modal.adjust_stock.error_title'),
        description: t('modal.adjust_stock.error_negative_stock', { available: currentQty }),
        variant: 'destructive',
      });
      return;
    }

    if (!reason.trim()) {
      toast({
        title: t('modal.adjust_stock.error_title'),
        description: t('modal.adjust_stock.error_no_reason'),
        variant: 'destructive',
      });
      return;
    }

    try {
      await adjustStock({
        product_id: productId,
        quantity,
        location_id: locationId,
      });

      toast({
        title: t('modal.adjust_stock.success_title'),
        description: t('modal.adjust_stock.success_description'),
        variant: 'success',
      });

      confirmRef.current();
      modalRef.current?.closeModal();
    } catch (error: unknown) {
      const err = error as { response?: { data?: Record<string, unknown> }; message?: string };
      const responseData = err?.response?.data;
      const apiMessage =
        (responseData?.message as string | undefined) ||
        (responseData?.error as { message?: string } | undefined)?.message ||
        err?.message;

      toast({
        title: t('modal.adjust_stock.error_title'),
        description: apiMessage || t('errors.unexpected'),
        variant: 'destructive',
      });
    }
  };

  return (
    <ModalCenter
      ref={modalRef}
      title={
        <span className="flex items-center gap-2">
          {t('modal.adjust_stock.title')}
          {locationCode && (
            <Badge variant="default" className="ml-2 font-normal text-xs text-white">
              <FontAwesomeIcon icon={faLocationDot} className="w-3 h-3 mr-1" />
              {locationCode}
            </Badge>
          )}
        </span>
      }
      type="default"
      width="lg"
      fullScreenMobile
    >
      <div className="space-y-6 pt-4 text-title">
        <div className="text-sm">
          <p className="mb-2">{t('modal.adjust_stock.note')}</p>
        </div>

        <div className="flex items-center justify-center gap-8 py-4 !mt-4">
          <div className="text-center">
            <div className="text-xl font-medium pb-2">{currentQty}</div>
            <div className="text-sm font-normal">{t('modal.adjust_stock.current_stock')}</div>
          </div>

          <FontAwesomeIcon icon={faArrowRight} className="w-5 h-5" />

          <div className="text-center">
            <div
              className={`text-xl font-medium pb-2 ${
                quantity === 0 ? '' : quantity > 0 ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {newStock}
            </div>
            <div className="text-sm font-normal">{t('modal.adjust_stock.new_stock')}</div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm mb-2 text-text">{t('modal.adjust_stock.adjustment')}</label>
              <StepperInput
                value={quantity}
                onChange={handleQuantityChange}
                min={minQuantity}
                max={99999}
                data-testid="warehouse-material-adjust-stock-quantity"
              />
              {quantity < 0 && (
                <p className="text-xs text-subtext mt-1">
                  {t('modal.adjust_stock.max_to_remove', { count: currentQty })}
                </p>
              )}
            </div>

            <div className="col-span-2">
              <label className="block text-sm mb-2 text-text">{t('modal.adjust_stock.reason_label')}</label>
              <Input
                placeholder={t('modal.adjust_stock.reason_placeholder')}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="warehouse-material-adjust-stock-reason"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" size="lg" onClick={() => modalRef.current?.closeModal()} disabled={isPending}>
            {t('button.cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleConfirm}
            disabled={quantity === 0 || isPending}
            data-testid="warehouse-material-adjust-stock-confirm"
          >
            {isPending ? t('button.saving') : t('button.confirm')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

AdjustStockModal.displayName = 'AdjustStockModal';
